/**
 * useState that lives in the URL query string, so admin list pages keep their
 * page, search, sort and filters when the user navigates away and comes back
 * (or refreshes, or shares the link).
 *
 * The "page swap resets my place" complaint was never caching: these values
 * were plain component state and vanished on unmount.
 *
 * - Defaults are NOT written to the URL, so a fresh page keeps a clean address.
 * - Updates use replace:true so Back does not walk through every keystroke.
 * - Functional updates (setPage(p => p + 1)) are supported.
 * - Two setters called in the same handler compose. react-router's functional setSearchParams
 *   hands every call the params from render time, so the second call used to erase the first
 *   (17 Sep 2026: picking a city set the text, then the coordinates overwrote it, or vice versa;
 *   "Clear filters" only cleared the last of three). Setters now start from the live URL.
 */
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export const stringCodec = { parse: v => v, format: v => v }
export const intCodec = { parse: v => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : NaN }, format: v => String(v) }

/** "lat,lng" <-> { latitude, longitude }; anything malformed or off the globe parses to NaN (= default). */
export const latLngCodec = {
  parse: v => {
    const parts = String(v ?? '').split(',')
    if (parts.length !== 2 || parts.some(x => x.trim() === '')) return NaN
    const latitude = Number(parts[0]), longitude = Number(parts[1])
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return NaN
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return NaN
    return { latitude, longitude }
  },
  format: v => `${v.latitude},${v.longitude}`
}

/** Enum-like string state with a fixed set of allowed values; anything else falls back. */
export function enumCodec(allowed, fallback) {
  return { parse: v => allowed.includes(v) ? v : fallback, format: v => v }
}

function decode(raw, defaultValue, codec) {
  if (raw === null || raw === undefined || raw === '') return defaultValue
  const parsed = codec.parse(raw)
  if (typeof parsed === 'number' && Number.isNaN(parsed)) return defaultValue
  return parsed
}

export function useSearchParamState(key, defaultValue, codec = stringCodec) {
  const [searchParams, setSearchParams] = useSearchParams()
  const raw = searchParams.get(key)

  const value = useMemo(() => decode(raw, defaultValue, codec), [raw, defaultValue, codec])

  const setValue = useCallback((next) => {
    setSearchParams(prev => {
      // Live URL first: a setter called right after another in the same handler must see its change.
      const base = typeof window !== 'undefined' && window.location ? new URLSearchParams(window.location.search) : prev
      const prevValue = decode(base.get(key), defaultValue, codec)
      const resolved = typeof next === 'function' ? next(prevValue) : next
      const params = new URLSearchParams(base)
      if (resolved === defaultValue || resolved === undefined || resolved === null || resolved === '') {
        params.delete(key)
      } else {
        params.set(key, codec.format(resolved))
      }
      return params
    }, { replace: true })
  }, [key, defaultValue, codec, setSearchParams])

  return [value, setValue]
}
